import clsx from 'clsx';
import { createContext, type ReactNode, useContext } from 'react';
import { useLinkClick } from '../../lib/router.ts';
import { navLink } from '../primitives/controls.tsx';
import { Hint, SECTION, TITLE } from '../primitives/shell.tsx';

// Caps, never centred: every page keeps the same left edge as Trades.
const WIDTHS = {
  prose: 'max-w-3xl',
  desk: 'max-w-5xl',
  wide: 'max-w-none',
  manual: 'max-w-[58rem]',
} as const;

/** Under a section's tabs the tab names the page, so a page there draws no title of its own. */
const UnderTabs = createContext(false);

/** A tabbed section: its one heading, then the tabs, then the tab's page. */
export function TabbedSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <h1 className={TITLE}>{title}</h1>
      <UnderTabs.Provider value={true}>{children}</UnderTabs.Provider>
    </div>
  );
}

export function Page({
  title,
  tabs,
  lead,
  aside,
  children,
  width = 'prose',
  heading: Heading = 'h1',
}: {
  title?: string;
  tabs?: ReactNode;
  lead?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  width?: keyof typeof WIDTHS;
  /** A page drawn inside another page heads itself one level down. */
  heading?: 'h1' | 'h2';
}) {
  const shown = useContext(UnderTabs) ? undefined : title;
  return (
    <div className={clsx('gap-page flex w-full flex-col', WIDTHS[width])}>
      {(shown || tabs || lead || aside) && (
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            {shown && <Heading className={Heading === 'h1' ? TITLE : SECTION}>{shown}</Heading>}
            {tabs}
            {lead && <p className="max-w-prose text-sm leading-relaxed text-ink-muted">{lead}</p>}
          </div>
          {aside}
        </header>
      )}
      {children}
    </div>
  );
}

export function Section({
  title,
  blurb,
  aside,
  children,
}: {
  title: string;
  blurb?: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className={SECTION}>{title}</h2>
        {aside}
      </div>
      {blurb && <Hint className="max-w-prose">{blurb}</Hint>}
      {children}
    </section>
  );
}

export function LinkTabs({
  label,
  tabs,
}: {
  label: string;
  tabs: readonly { href: string; here: boolean; label: ReactNode }[];
}) {
  const onClick = useLinkClick();
  if (tabs.length === 0) return null;
  return (
    <nav aria-label={label} className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1">
      {tabs.map((tab) => (
        <a
          key={tab.href}
          href={tab.href}
          onClick={onClick}
          aria-current={tab.here ? 'page' : undefined}
          className={navLink(tab.here)}
        >
          {tab.label}
        </a>
      ))}
    </nav>
  );
}
